<template>
  <div class="member-passes-wrap">
    <v-row>
      <!-- ── Active Gate Passes ──────────────── -->
      <v-col cols="12" md="6">
        <PremiumTable
          title="Active Gate Passes"
          :columns="activePassColumns"
          :items="gate_passes.active"
          :loading="loading"
          :total-items="activeTotal"
          :page.sync="activeOptions.page"
          :items-per-page.sync="activeOptions.itemsPerPage"
          :searchable="false"
          @update:page="
            (p) => {
              activeOptions.page = p;
              fetchGatePasses();
            }
          "
          @update:items-per-page="
            (p) => {
              activeOptions.itemsPerPage = p;
              fetchGatePasses();
            }
          "
          @update:options="onActiveOptionsUpdate"
        >
          <template #col-visitor_name="{ item }">
            <span class="visitor-name-primary">{{ item.visitor_name }}</span>
          </template>

          <template #col-pass_type="{ item }">
            <span class="pass-type-pill">
              {{ item.pass_type }}
            </span>
          </template>

          <template #col-validity="{ item }">
            <div class="validity-display-wrap">
              <span class="validity-main">{{ getValidityDisplay(item) }}</span>
              <div v-if="hasExtraConfig(item)" class="validity-sub">
                {{ getExtraConfigLabel(item) }}
              </div>
            </div>
          </template>
        </PremiumTable>
      </v-col>

      <!-- ── Expired / Closed ────────────────── -->
      <v-col cols="12" md="6">
        <PremiumTable
          title="Expired / History"
          :columns="expiredPassColumns"
          :items="gate_passes.expired"
          :loading="loading"
          :total-items="expiredTotal"
          :page.sync="expiredOptions.page"
          :items-per-page.sync="expiredOptions.itemsPerPage"
          :searchable="false"
          @update:page="
            (p) => {
              expiredOptions.page = p;
              fetchGatePasses();
            }
          "
          @update:items-per-page="
            (p) => {
              expiredOptions.itemsPerPage = p;
              fetchGatePasses();
            }
          "
          @update:options="onExpiredOptionsUpdate"
        >
          <template #col-visitor_name="{ item }">
            <span class="visitor-name-muted">{{ item.visitor_name }}</span>
          </template>

          <template #col-status="{ item }">
            <span class="status-badge-mini" :class="'s-' + item.status">
              {{ item.status.toUpperCase() }}
            </span>
          </template>

          <template #col-expired_date="{ item }">
            <span class="date-text-mini">{{
              (item.valid_till_date || item.valid_from_date) | formatDate
            }}</span>
          </template>
        </PremiumTable>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import PremiumTable from "../../common/PremiumTable.vue";

export default {
  name: "MemberGatePasses",

  components: { PremiumTable },

  props: {
    unit_mapping_id: { type: [String, Number], required: true },
  },

  data() {
    return {
      loading: false,
      gate_passes: {
        active: [],
        expired: [],
      },
      activeTotal: 0,
      activeOptions: { page: 1, itemsPerPage: 5 },
      expiredTotal: 0,
      expiredOptions: { page: 1, itemsPerPage: 5 },
      activePassColumns: [
        { key: "visitor_name", label: "Visitor", width: "180px" },
        { key: "pass_type", label: "Pass Type", width: "120px" },
        { key: "validity", label: "Valid Till" },
      ],
      expiredPassColumns: [
        { key: "visitor_name", label: "Visitor" },
        { key: "expired_date", label: "Expired On", width: "120px" },
        { key: "status", label: "Status", width: "100px" },
      ],
    };
  },

  methods: {
    onActiveOptionsUpdate() {
      this.fetchGatePasses();
    },
    onExpiredOptionsUpdate() {
      this.fetchGatePasses();
    },

    async fetchGatePasses() {
      this.loading = true;
      try {
        const response = await this.$axios.get(
          `societyadmin/members/${this.unit_mapping_id}/gatepasses`,
          {
            params: {
              activePage: this.activeOptions.page,
              expiredPage: this.expiredOptions.page,
              activePerPage: this.activeOptions.itemsPerPage,
              expiredPerPage: this.expiredOptions.itemsPerPage,
              perPage: 5,
            },
          },
        );
        if (response.data.status) {
          this.gate_passes = response.data.gate_passes;
          this.activeTotal = response.data.gate_passes.activeTotal;
          this.expiredTotal = response.data.gate_passes.expiredTotal;
        }
      } catch (error) {
        console.error("Error fetching gate passes:", error);
      } finally {
        this.loading = false;
      }
    },

    getValidityDisplay(item) {
      if (item.valid_till_type === "never_expire" || (!item.valid_from_date && !item.valid_till_date)) {
        return "Never Expires";
      }

      // If it's a one-time pass, just show the single date
      if (item.pass_type === "One-time" || item.pass_type === "one_time") {
        return item.valid_from_date ? this.$options.filters.formatDate(item.valid_from_date) : "Today";
      }

      const from = item.valid_from_date ? this.$options.filters.formatDate(item.valid_from_date) : "Now";
      const to = item.valid_till_date ? this.$options.filters.formatDate(item.valid_till_date) : "Forever";

      if (from === to && from !== "Now") return from;
      return `${from} → ${to}`;
    },

    hasExtraConfig(item) {
      return item.recurrence_config || item.valid_from_time || item.allow_multiple_entry;
    },

    getExtraConfigLabel(item) {
      let labels = [];
      
      // Recurrence
      if (item.recurrence_config) {
        const config = item.recurrence_config;
        if (config.type === 'daily') labels.push('Daily');
        else if (config.type === 'weekly' && config.days) labels.push(config.days.join(', '));
        else if (config.type === 'monthly') labels.push('Monthly');
      }

      // Time slots
      if (item.valid_from_time && item.valid_till_time) {
        labels.push(`${item.valid_from_time} - ${item.valid_till_time}`);
      }

      // Multiple Entry
      if (item.allow_multiple_entry) {
        labels.push('Multiple Entry');
      }

      return labels.length > 0 ? labels.join(' • ') : '';
    },


  },

  mounted() {
    this.fetchGatePasses();
  },
};
</script>

<style scoped>
.table-header-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.th-title-wrap {
  display: flex;
  align-items: center;
}
.th-title {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--tx);
  letter-spacing: -0.01em;
  margin: 0;
}

/* ── Active Pass Cells ── */
.visitor-name-primary {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--tx);
  text-transform: capitalize;
}
.pass-type-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
}
.pass-type-pill::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
}

.validity-display-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.validity-main {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx);
  white-space: nowrap;
}
.validity-sub {
  font-size: 11px;
  color: var(--brand);
  font-weight: 600;
  letter-spacing: 0.01em;
}

/* ── Expired Pass Cells ── */
.visitor-name-muted {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx3);
  text-transform: capitalize;
}
.date-text-mini {
  font-size: 11.5px;
  color: var(--tx3);
  font-weight: 600;
}

.status-badge-mini {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.status-badge-mini::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.s-expired {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}
.s-expired::before { background: #ef4444; }
.s-used {
  background: rgba(100, 116, 139, 0.1);
  color: #64748b;
}
.s-used::before { background: #94a3b8; }
</style>
